import { useEffect, useState } from "react";
import { fetchIdentityConflicts } from "../lib/api";

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls the identity-conflicts endpoint to keep a live count of OPEN
 * conflicts available for the sidebar nav badge. Refreshes every 60s
 * and on window focus so an operator who resolves a conflict in another
 * tab (or on their phone) sees the badge update without a full reload.
 *
 * Returns null while the first fetch is in flight. A network failure is
 * silently swallowed so the badge just stops updating - it should never
 * cause the sidebar to error out.
 */
export function useOpenIdentityConflictsCount(): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const conflicts = await fetchIdentityConflicts();
        if (cancelled) return;
        setCount(conflicts.filter((c) => c.status === "open").length);
      } catch {
        // swallowed; badge will just stop updating until next interval
      }
    }

    void load();
    const handle = window.setInterval(load, POLL_INTERVAL_MS);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return count;
}
